import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import TEAMS from "../data/teams";

const C = {
  surf:"#161b22", card:"#1c2330", bdr:"#30363d",
  txt:"#e6edf3", muted:"#7d8590",
  blue:"#388bfd", grn:"#3fb950", red:"#f85149", ylw:"#e3b341", org:"#f97316",
};

function getTeamColor(teamId) {
  const t = TEAMS.find(t => t.id === teamId);
  return t ? t.color : C.muted;
}
function getTeamAbbr(teamId) {
  const t = TEAMS.find(t => t.id === teamId);
  return t ? t.abbr : "?";
}

function calcAvg(h, ab) {
  if (!ab) return ".000";
  const v = (h / ab).toFixed(3);
  return v.startsWith("1") ? "1.000" : v.replace(/^0/, "");
}

function calcOBP(h, bb, ab) {
  const num = (h || 0) + (bb || 0);
  const den = (ab || 0) + (bb || 0);
  if (!den) return ".000";
  const v = (num / den).toFixed(3);
  return v.startsWith("1") ? "1.000" : v.replace(/^0/, "");
}

function calcSLG(s1, s2, s3, hr, ab) {
  if (!ab) return ".000";
  const tb = (s1 || 0) + 2*(s2 || 0) + 3*(s3 || 0) + 4*(hr || 0);
  const v  = (tb / ab).toFixed(3);
  return v.startsWith("1") ? "1.000" : v.replace(/^0/, "");
}

function calcOPS(obp, slg) {
  const o = parseFloat("0" + obp);
  const s = parseFloat("0" + slg);
  const v = (o + s).toFixed(3);
  if (parseFloat(v) >= 1) return v;
  return v.replace(/^0/, "");
}

function calcERA(er, outs) {
  if (!outs) return "0.00";
  const ip = outs / 2; // 2 outs = 1 inning in this league
  return ((er / ip) * 9).toFixed(2);
}

// IP display: 2 outs = 1 full inning, 1 out = 0.1
// e.g. 0 outs = 0, 1 out = 0.1, 2 outs = 1, 3 outs = 1.1, 4 outs = 2
function calcIP(outs) {
  if (!outs) return "0";
  const full = Math.floor(outs / 2);
  const rem  = outs % 2;
  if (rem === 0) return `${full}`;
  return `${full}.1`;
}

export default function Stats() {
  const [tab,     setTab]     = useState("batting");
  const [stats,   setStats]   = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchStats() {
      try {
        const ref  = doc(db, "league", "playerStats");
        const snap = await getDoc(ref);
        if (snap.exists()) setStats(snap.data());
      } catch (err) {
        console.error("Error fetching stats:", err);
      }
      setLoading(false);
    }
    fetchStats();
  }, []);

  const allPlayers = [];
  TEAMS.forEach(team => {
    team.players.forEach(player => {
      const key = `${team.id}|${player}`;
      const s   = stats[key] || {};
      allPlayers.push({ teamId: team.id, player, key, ...s });
    });
  });

  const battingRows = allPlayers.map(p => {
    const ab  = p.AB    || 0;
    const h   = p.H     || 0;
    const bb  = p.BB    || 0;
    const s1  = p["1B"] || 0;
    const s2  = p["2B"] || 0;
    const s3  = p["3B"] || 0;
    const hr  = p.HR    || 0;
    const rbi = p.RBI   || 0;
    const obp = calcOBP(h, bb, ab);
    const slg = calcSLG(s1, s2, s3, hr, ab);
    return {
      ...p, ab, h, bb, s1, s2, s3, hr, rbi,
      AVG: calcAvg(h, ab),
      OBP: obp,
      SLG: slg,
      OPS: calcOPS(obp, slg),
    };
  }).sort((a, b) => b.h - a.h || b.ab - a.ab);

  const pitchingRows = allPlayers.map(p => {
    const outs = p.outs || 0;
    const so   = p.SO   || 0;
    const er   = p.ER   || 0;
    return {
      ...p, outs, so, er,
      IP:  calcIP(outs),
      ERA: calcERA(er, outs),
    };
  }).sort((a, b) => b.outs - a.outs || a.er - b.er);

  if (loading) return (
    <p style={{ color:C.muted, textAlign:"center", padding:40 }}>Loading stats...</p>
  );

  const noGames = allPlayers.every(p => !stats[p.key]);

  return (
    <div style={{ padding:"20px 14px" }}>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6, marginBottom:18 }}>
        {[{id:"batting",label:"Batting"},{id:"pitching",label:"Pitching"}].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ background: tab===t.id ? C.blue : C.card, color: tab===t.id ? "#fff" : C.muted, border:`1px solid ${tab===t.id ? C.blue : C.bdr}`, borderRadius:8, padding:"10px 0", fontSize:14, fontWeight:700, cursor:"pointer" }}>
            {t.label}
          </button>
        ))}
      </div>

      {noGames ? (
        <div style={{ textAlign:"center", padding:"40px 0" }}>
          <p style={{ fontSize:36, margin:"0 0 12px" }}>📊</p>
          <p style={{ color:C.txt, fontSize:16, fontWeight:600, margin:"0 0 6px" }}>No stats yet</p>
          <p style={{ color:C.muted, fontSize:14, margin:0 }}>Stats will appear here after games are saved.</p>
        </div>
      ) : tab === "batting" ? (
        <BattingTable rows={battingRows} />
      ) : (
        <PitchingTable rows={pitchingRows} />
      )}
    </div>
  );
}

// ── Batting Table ──────────────────────────────────────────────────────────────

function BattingTable({ rows }) {
  const [sort, setSort] = useState("h");

  const cols = [
    { id:"h",   label:"H",   color: C.grn },
    { id:"AVG", label:"AVG", color: C.blue },
    { id:"OBP", label:"OBP", color: C.blue },
    { id:"SLG", label:"SLG", color: C.org },
    { id:"OPS", label:"OPS", color: C.ylw },
    { id:"hr",  label:"HR",  color: C.red },
    { id:"s2",  label:"2B",  color: C.muted },
    { id:"s3",  label:"3B",  color: C.muted },
    { id:"rbi", label:"RBI", color: C.muted },
    { id:"bb",  label:"BB",  color: C.muted },
    { id:"ab",  label:"AB",  color: C.muted },
  ];

  function statVal(p, id) {
    if (["AVG","OBP","SLG","OPS"].includes(id)) {
      return parseFloat("0" + p[id]);
    }
    return p[id] || 0;
  }

  const sorted = [...rows].sort((a, b) => statVal(b, sort) - statVal(a, sort));

  return (
    <div>
      {/* Sort buttons */}
      <div style={{ display:"flex", gap:5, marginBottom:12, flexWrap:"wrap" }}>
        <span style={{ color:C.muted, fontSize:12, alignSelf:"center", marginRight:2 }}>Sort:</span>
        {cols.map(c => (
          <button key={c.id} onClick={() => setSort(c.id)}
            style={{ background: sort===c.id ? c.color+"33" : C.card, color: sort===c.id ? c.color : C.muted, border:`1px solid ${sort===c.id ? c.color+"66" : C.bdr}`, borderRadius:6, padding:"4px 9px", fontSize:11, fontWeight:700, cursor:"pointer" }}>
            {c.label}
          </button>
        ))}
      </div>

      {/* Header */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 30px 44px 44px 44px 48px 28px 28px 28px 28px 30px", gap:3, padding:"8px 10px", background:C.card, borderRadius:"8px 8px 0 0", border:`1px solid ${C.bdr}`, borderBottom:"none", overflowX:"auto" }}>
        {["PLAYER","AB","AVG","OBP","SLG","OPS","H","HR","2B","3B","RBI"].map((h, i) => (
          <span key={h} style={{ color:C.muted, fontSize:10, fontWeight:700, textAlign: i===0?"left":"center" }}>{h}</span>
        ))}
      </div>

      <div style={{ border:`1px solid ${C.bdr}`, borderRadius:"0 0 8px 8px", overflow:"hidden" }}>
        {sorted.map((p, i) => (
          <div key={p.key} style={{ display:"grid", gridTemplateColumns:"1fr 30px 44px 44px 44px 48px 28px 28px 28px 28px 30px", gap:3, padding:"11px 10px", background: i%2===0 ? C.surf : C.card, borderBottom: i < sorted.length-1 ? `1px solid ${C.bdr}` : "none", alignItems:"center" }}>
            <div style={{ display:"flex", alignItems:"center", gap:7, minWidth:0 }}>
              <div style={{ width:3, height:26, borderRadius:2, background:getTeamColor(p.teamId), flexShrink:0 }} />
              <div style={{ minWidth:0 }}>
                <p style={{ color:C.txt, fontSize:12, fontWeight:600, margin:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.player}</p>
                <p style={{ color:C.muted, fontSize:10, margin:0 }}>{getTeamAbbr(p.teamId)}</p>
              </div>
            </div>
            <span style={{ color:C.muted, fontSize:12, textAlign:"center" }}>{p.ab}</span>
            <span style={{ color:C.blue,  fontSize:12, fontWeight:700, textAlign:"center" }}>{p.AVG}</span>
            <span style={{ color:C.blue,  fontSize:12, fontWeight:700, textAlign:"center" }}>{p.OBP}</span>
            <span style={{ color:C.org,   fontSize:12, fontWeight:700, textAlign:"center" }}>{p.SLG}</span>
            <span style={{ color:C.ylw,   fontSize:12, fontWeight:700, textAlign:"center" }}>{p.OPS}</span>
            <span style={{ color:C.grn,   fontSize:12, fontWeight:700, textAlign:"center" }}>{p.h}</span>
            <span style={{ color:C.red,   fontSize:12, fontWeight:700, textAlign:"center" }}>{p.hr}</span>
            <span style={{ color:C.muted, fontSize:12, textAlign:"center" }}>{p.s2}</span>
            <span style={{ color:C.muted, fontSize:12, textAlign:"center" }}>{p.s3}</span>
            <span style={{ color:C.muted, fontSize:12, textAlign:"center" }}>{p.rbi}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Pitching Table ─────────────────────────────────────────────────────────────

function PitchingTable({ rows }) {
  const [sort, setSort] = useState("IP");

  const cols = [
    { id:"IP",  label:"IP",  color: C.blue },
    { id:"SO",  label:"SO",  color: C.grn },
    { id:"ER",  label:"ER",  color: C.org },
    { id:"ERA", label:"ERA", color: C.red },
  ];

  const sorted = [...rows].sort((a, b) => {
    if (sort === "ERA") return parseFloat(a.ERA) - parseFloat(b.ERA);
    if (sort === "IP")  return b.outs - a.outs;
    return (b[sort.toLowerCase()] || 0) - (a[sort.toLowerCase()] || 0);
  });

  return (
    <div>
      <div style={{ display:"flex", gap:5, marginBottom:12, flexWrap:"wrap" }}>
        <span style={{ color:C.muted, fontSize:12, alignSelf:"center", marginRight:2 }}>Sort:</span>
        {cols.map(c => (
          <button key={c.id} onClick={() => setSort(c.id)}
            style={{ background: sort===c.id ? c.color+"33" : C.card, color: sort===c.id ? c.color : C.muted, border:`1px solid ${sort===c.id ? c.color+"66" : C.bdr}`, borderRadius:6, padding:"4px 9px", fontSize:11, fontWeight:700, cursor:"pointer" }}>
            {c.label}
          </button>
        ))}
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 48px 48px 48px 56px", gap:4, padding:"8px 10px", background:C.card, borderRadius:"8px 8px 0 0", border:`1px solid ${C.bdr}`, borderBottom:"none" }}>
        {["PLAYER","IP","SO","ER","ERA"].map((h, i) => (
          <span key={h} style={{ color:C.muted, fontSize:10, fontWeight:700, textAlign: i===0?"left":"center" }}>{h}</span>
        ))}
      </div>

      <div style={{ border:`1px solid ${C.bdr}`, borderRadius:"0 0 8px 8px", overflow:"hidden" }}>
        {sorted.map((p, i) => (
          <div key={p.key} style={{ display:"grid", gridTemplateColumns:"1fr 48px 48px 48px 56px", gap:4, padding:"11px 10px", background: i%2===0 ? C.surf : C.card, borderBottom: i < sorted.length-1 ? `1px solid ${C.bdr}` : "none", alignItems:"center" }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, minWidth:0 }}>
              <div style={{ width:3, height:26, borderRadius:2, background:getTeamColor(p.teamId), flexShrink:0 }} />
              <div style={{ minWidth:0 }}>
                <p style={{ color:C.txt, fontSize:12, fontWeight:600, margin:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.player}</p>
                <p style={{ color:C.muted, fontSize:10, margin:0 }}>{getTeamAbbr(p.teamId)}</p>
              </div>
            </div>
            <span style={{ color:C.blue, fontSize:12, fontWeight:700, textAlign:"center" }}>{p.IP}</span>
            <span style={{ color:C.grn,  fontSize:12, fontWeight:700, textAlign:"center" }}>{p.so}</span>
            <span style={{ color:C.org,  fontSize:12, fontWeight:700, textAlign:"center" }}>{p.er}</span>
            <span style={{ color:C.red,  fontSize:12, fontWeight:700, textAlign:"center" }}>{p.ERA}</span>
          </div>
        ))}
      </div>
    </div>
  );
}